import { FC, useContext, useEffect, useRef, useState } from "react";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import { useUserSettings } from "../../context/userSettingsContext";
import useDataInitializer from "../../hooks/useDataInitializer";
import { useI18n } from "../../hooks/useTranslations";
import { useHighwayPassesApi } from "../../http/highwayPasses/api";
import { UpsertHighwayPass } from "../../http/highwayPasses/types";
import ModalDialog from "../ui/ModalDialog";
import HighwayPassForm, { HighwayPassFormBody } from "./HighwayPassForm";

type EditHighwayPassDialogProps = {
  highwayPassId: string;
  onClose: (success?: boolean) => void;
  carId?: string;
};

const EditHighwayPassDialog: FC<EditHighwayPassDialogProps> = ({
  highwayPassId,
  onClose,
  carId,
}: EditHighwayPassDialogProps) => {
  const i18n = useI18n();
  const aborter = useRef(new AbortController());
  const userSettings = useUserSettings();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const highwayPassesApi = useHighwayPassesApi();

  const highwayPassSwr = highwayPassesApi.getAdminHighwayPass(highwayPassId, aborter.current.signal);
  const [highwayPass, isInitialized] = useDataInitializer(highwayPassSwr.data, highwayPassSwr.error);

  useEffect(() => {
    highwayPassId && highwayPassSwr.trigger();
  }, [highwayPassId]);

  useEffect(() => {
    highwayPassSwr.error && dispatch(showErrorNotification(i18n, highwayPassSwr.error));
  }, [highwayPassSwr.error]);

  const onSubmit = async (formData: HighwayPassFormBody): Promise<void> => {
    if (highwayPass?.id) {
      const body: UpsertHighwayPass = {
        carId: carId ?? formData.car.id,
        nr: formData.nr,
        startDate: formData.startDate,
        endDate: formData.endDate,
        currentVersionNumber: highwayPass?.versionNumber,
      };

      setIsSubmitting(true);
      try {
        await highwayPassesApi.updateAdminHighwayPass(highwayPass.id, body);
        dispatch(showSuccessNotification(i18n.t));
        onClose(true);
      } catch (err) {
        dispatch(showErrorNotification(i18n, err));
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <>
      <ModalDialog onClose={onClose} title={i18n.t.highwayPasses.edit}>
        <HighwayPassForm
          onSubmit={onSubmit}
          isDisabled={!userSettings.permissions.highwayPasses.update || isSubmitting || !highwayPass?.id}
          isLoading={!isInitialized}
          highwayPass={highwayPass}
          allowSelectCar={!carId}
        />
      </ModalDialog>
    </>
  );
};

export default EditHighwayPassDialog;
